import { useState, useEffect, useCallback } from 'react';
import posthog from 'posthog-js';
import {
  CookieConsent,
  getStoredConsent,
  getDefaultConsent,
  saveConsent,
  acceptAllCookies,
  rejectNonEssentialCookies,
  hasConsented,
} from '@/lib/cookieConsent';

interface UseCookieConsentReturn {
  consent: CookieConsent;
  showBanner: boolean;
  hasConsented: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  updateConsent: (updates: Partial<Omit<CookieConsent, 'essential' | 'timestamp' | 'version'>>) => void;
  openPreferences: () => void;
  closePreferences: () => void;
  showPreferences: boolean;
}

export const useCookieConsent = (): UseCookieConsentReturn => {
  const [consent, setConsent] = useState<CookieConsent>(getDefaultConsent);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setConsent(stored);
      setShowBanner(false);
    } else {
      // Show banner after a short delay for better UX
      const timeout = setTimeout(() => {
        setShowBanner(true);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, []);

  const acceptAll = useCallback(() => {
    const newConsent = acceptAllCookies();
    setConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
    if (newConsent.analytics) posthog.opt_in_capturing();
  }, []);

  const rejectNonEssential = useCallback(() => {
    const newConsent = rejectNonEssentialCookies();
    setConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
    posthog.opt_out_capturing();
  }, []);

  const updateConsentHandler = useCallback((
    updates: Partial<Omit<CookieConsent, 'essential' | 'timestamp' | 'version'>>
  ) => {
    const newConsent: CookieConsent = {
      ...consent,
      ...updates,
      essential: true,
      timestamp: new Date().toISOString(),
      version: consent.version,
    };
    saveConsent(newConsent);
    setConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
    if (newConsent.analytics) posthog.opt_in_capturing();
    else posthog.opt_out_capturing();
  }, [consent]);

  const openPreferences = useCallback(() => {
    setShowPreferences(true);
  }, []);

  const closePreferences = useCallback(() => {
    setShowPreferences(false);
  }, []);

  return {
    consent,
    showBanner,
    hasConsented: hasConsented(),
    acceptAll,
    rejectNonEssential,
    updateConsent: updateConsentHandler,
    openPreferences,
    closePreferences,
    showPreferences,
  };
};
